import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Button,
  Tooltip,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  _TextField,
  Typography,
  _Badge,
} from '@mui/material';
import {
  Code as CodeIcon,
  _Delete,
  _Add,
  _Edit,
  _Close,
  AddCircle,
  RemoveCircle,
  ChangeCircle,
  History as HistoryIcon,
} from '@mui/icons-material';
import React, { useCallback, useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import callApi from 'client/util/apiCaller';
import { useDispatch } from 'react-redux';

const messages = defineMessages({
  title: {
    id: 'AuditLog.Title',
    defaultMessage: 'Audit Log',
  },
  description: {
    id: 'AuditLog.Description',
    defaultMessage:
      'View a complete history of changes made to your organization and surveys. All changes are tracked automatically.',
  },
  loading: {
    id: 'AuditLog.Loading',
    defaultMessage: 'Loading...',
  },
  noLogs: {
    id: 'AuditLog.NoLogs',
    defaultMessage: 'No audit logs found.',
  },
  error: {
    id: 'AuditLog.Error',
    defaultMessage: 'Error loading audit logs. Please try again.',
  },
  timestamp: {
    id: 'AuditLog.Timestamp',
    defaultMessage: 'Timestamp',
  },
  user: {
    id: 'AuditLog.User',
    defaultMessage: 'User',
  },
  action: {
    id: 'AuditLog.Action',
    defaultMessage: 'Action',
  },
  entity: {
    id: 'AuditLog.Entity',
    defaultMessage: 'Entity',
  },
  explanation: {
    id: 'AuditLog.Explanation',
    defaultMessage: 'Explanation',
  },
  filterCollection: {
    id: 'AuditLog.FilterCollection',
    defaultMessage: 'Filter by type',
  },
  allTypes: {
    id: 'AuditLog.AllTypes',
    defaultMessage: 'All Types',
  },
  organizations: {
    id: 'AuditLog.Organizations',
    defaultMessage: 'Organizations',
  },
  surveys: {
    id: 'AuditLog.Surveys',
    defaultMessage: 'Surveys',
  },
  operationInsert: {
    id: 'AuditLog.OperationInsert',
    defaultMessage: 'Created',
  },
  operationUpdate: {
    id: 'AuditLog.OperationUpdate',
    defaultMessage: 'Updated',
  },
  operationDelete: {
    id: 'AuditLog.OperationDelete',
    defaultMessage: 'Deleted',
  },
  operationReplace: {
    id: 'AuditLog.OperationReplace',
    defaultMessage: 'Replaced',
  },
  unknownUser: {
    id: 'AuditLog.UnknownUser',
    defaultMessage: 'Automated Action',
  },
  noExplanation: {
    id: 'AuditLog.NoExplanation',
    defaultMessage: 'No explanation available',
  },
  viewJson: {
    id: 'AuditLog.ViewJson',
    defaultMessage: 'View JSON',
  },
  changesJsonTitle: {
    id: 'AuditLog.ChangesJsonTitle',
    defaultMessage: 'Changes JSON',
  },
  copy: {
    id: 'AuditLog.Copy',
    defaultMessage: 'Copy',
  },
  close: {
    id: 'AuditLog.Close',
    defaultMessage: 'Close',
  },
  viewDocumentBefore: {
    id: 'AuditLog.ViewDocumentBefore',
    defaultMessage: 'View Document Before',
  },
  documentBeforeTitle: {
    id: 'AuditLog.DocumentBeforeTitle',
    defaultMessage: 'Document Before Change',
  },
  loadingSnapshot: {
    id: 'AuditLog.LoadingSnapshot',
    defaultMessage: 'Loading snapshot...',
  },
  snapshotError: {
    id: 'AuditLog.SnapshotError',
    defaultMessage: 'Error loading snapshot. It may have expired or been deleted.',
  },
  snapshotNotAvailable: {
    id: 'AuditLog.SnapshotNotAvailable',
    defaultMessage: 'Snapshot not available',
  },
});

function AuditLogPage() {
  const { formatMessage, formatDate, formatTime, locale } = useIntl();
  const dispatch = useDispatch();
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ total: 0, limit: 10, skip: 0, hasMore: false });
  const [page, setPage] = useState(1);
  const [collectionFilter, setCollectionFilter] = useState('all');
  const [jsonDialogOpen, setJsonDialogOpen] = useState(false);
  const [jsonDialogContent, setJsonDialogContent] = useState('');
  const [documentBeforeDialogOpen, setDocumentBeforeDialogOpen] = useState(false);
  const [documentBeforeContent, setDocumentBeforeContent] = useState(null);
  const [documentBeforeLoading, setDocumentBeforeLoading] = useState(false);
  const [documentBeforeError, setDocumentBeforeError] = useState(null);

  const fetchAuditLogs = useCallback(
    async (skip = 0, collectionName = null) => {
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams({
          limit: '10',
          skip: skip.toString(),
        });

        if (collectionName && collectionName !== 'all') {
          params.append('collectionName', collectionName);
        }

        const response = await callApi(dispatch, `audit?${params.toString()}`);

        if (response && response.auditLogs) {
          setAuditLogs(response.auditLogs || []);
          setPagination(response.pagination || { total: 0, limit: 10, skip: 0, hasMore: false });
        } else {
          setError(formatMessage(messages.error));
        }
      } catch (err) {
        console.error('Error fetching audit logs:', err);
        setError(formatMessage(messages.error));
      } finally {
        setLoading(false);
      }
    },
    [dispatch, formatMessage],
  );

  const sensitiveKeys = new Set([
    'secret',
    'apiKey',
    'accessToken',
    'refreshToken',
    'token',
    'clientSecret',
    'password',
    'webhookSecret',
    'privateKey',
  ]);

  const redactSensitive = value => {
    if (Array.isArray(value)) {
      return value.map(v => redactSensitive(v));
    }
    if (value && typeof value === 'object') {
      const ret = {};
      Object.entries(value).forEach(([k, v]) => {
        const keyLower = (k || '').toString().toLowerCase();
        if (sensitiveKeys.has(keyLower)) {
          ret[k] = '********';
        } else {
          ret[k] = redactSensitive(v);
        }
      });
      return ret;
    }
    return value;
  };

  const openChangesJson = log => {
    const redacted = redactSensitive(log?.changes || {});
    const jsonStr = JSON.stringify(redacted, null, 2);
    setJsonDialogContent(jsonStr);
    setJsonDialogOpen(true);
  };

  const copyJsonToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(jsonDialogContent);
    } catch (_e) {
      // no-op
    }
  };

  const openDocumentBefore = async log => {
    setDocumentBeforeDialogOpen(true);
    setDocumentBeforeLoading(true);
    setDocumentBeforeError(null);
    setDocumentBeforeContent(null);

    try {
      const response = await callApi(dispatch, `audit/${log._id}/snapshot`);
      if (response && response.documentBefore) {
        const redacted = redactSensitive(response.documentBefore);
        const jsonStr = JSON.stringify(redacted, null, 2);
        setDocumentBeforeContent(jsonStr);
      } else {
        setDocumentBeforeError(formatMessage(messages.snapshotNotAvailable));
      }
    } catch (err) {
      console.error('Error fetching documentBefore snapshot:', err);
      setDocumentBeforeError(formatMessage(messages.snapshotError));
    } finally {
      setDocumentBeforeLoading(false);
    }
  };

  const copyDocumentBeforeToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(documentBeforeContent);
    } catch (_e) {
      // no-op
    }
  };

  useEffect(() => {
    fetchAuditLogs(0, collectionFilter === 'all' ? null : collectionFilter);
    setPage(1);
  }, [collectionFilter, fetchAuditLogs]);

  useEffect(() => {
    const skip = (page - 1) * pagination.limit;
    fetchAuditLogs(skip, collectionFilter === 'all' ? null : collectionFilter);
  }, [collectionFilter, fetchAuditLogs, page, pagination.limit]);

  const _getOperationLabel = operationType => {
    const operationMap = {
      insert: 'operationInsert',
      update: 'operationUpdate',
      delete: 'operationDelete',
      replace: 'operationReplace',
    };
    return formatMessage(messages[operationMap[operationType] || 'operationUpdate']);
  };

  const getUserDisplay = log => {
    if (log.userName) return log.userName;
    if (log.userEmail) return log.userEmail;
    if (log.userId) return log.userId;
    return formatMessage(messages.unknownUser);
  };

  const getSurveyDisplay = log => {
    if (log.surveyName) return log.surveyName;
    if (log.surveyBrand) return log.surveyBrand;
    return log.documentId;
  };

  const getOrganizationDisplay = log => {
    if (log.organizationName) return log.organizationName;
    return log.documentId;
  };

  const getExplanation = log => {
    if (!log.explanation) return formatMessage(messages.noExplanation);

    // Get explanation in current locale (en or es)
    const lang = locale.startsWith('es') ? 'es' : 'en';
    const explanation = log.explanation[lang] || log.explanation.en || log.explanation.text || '';

    if (!explanation) return formatMessage(messages.noExplanation);

    // Replace placeholders
    const userDisplay = getUserDisplay(log);
    const surveyDisplay = getSurveyDisplay(log);

    let result = explanation.replace(/\{\{user\}\}/g, userDisplay);
    result = result.replace(/\{\{survey\}\}/g, surveyDisplay);
    result = result.replace(/\{\{organization\}\}/g, getOrganizationDisplay(log));

    return result;
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleCollectionFilterChange = event => {
    setCollectionFilter(event.target.value);
  };

  const totalPages = Math.ceil(pagination.total / pagination.limit);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        {formatMessage(messages.title)}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {formatMessage(messages.description)}
      </Typography>

      <Card sx={{ mt: 3, mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>{formatMessage(messages.filterCollection)}</InputLabel>
              <Select
                value={collectionFilter}
                label={formatMessage(messages.filterCollection)}
                onChange={handleCollectionFilterChange}
              >
                <MenuItem value="all">{formatMessage(messages.allTypes)}</MenuItem>
                <MenuItem value="organizations">{formatMessage(messages.organizations)}</MenuItem>
                <MenuItem value="surveys">{formatMessage(messages.surveys)}</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : auditLogs.length === 0 ? (
            <Alert severity="info">{formatMessage(messages.noLogs)}</Alert>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{formatMessage(messages.timestamp)}</TableCell>
                      <TableCell>{formatMessage(messages.entity)}</TableCell>
                      <TableCell>{formatMessage(messages.explanation)}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {auditLogs.map(log => {
                      const date = new Date(log.timestamp);
                      const dateStr = formatDate(date, {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      });
                      const timeStr = formatTime(date);
                      return (
                        <TableRow key={log._id}>
                          <TableCell>
                            <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                              {dateStr}
                            </Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                              {timeStr}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={
                                log.collectionName === 'organizations'
                                  ? formatMessage(messages.organizations)
                                  : formatMessage(messages.surveys)
                              }
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                              {log.operationType === 'insert' && (
                                <AddCircle
                                  fontSize="6"
                                  sx={{ transform: 'translateY(3px)', color: 'success.dark' }}
                                />
                              )}
                              {log.operationType === 'delete' && (
                                <RemoveCircle
                                  fontSize="6"
                                  sx={{ transform: 'translateY(3px)', color: 'error.dark' }}
                                />
                              )}
                              {log.operationType === 'update' && (
                                <ChangeCircle
                                  fontSize="6"
                                  sx={{ transform: 'translateY(3px)', color: 'grey.700' }}
                                />
                              )}
                              <Typography variant="body2" sx={{ flex: 1 }}>
                                {getExplanation(log)}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 0.5, alignSelf: 'center' }}>
                                <Tooltip title={formatMessage(messages.viewJson)} arrow>
                                  <IconButton
                                    aria-label={formatMessage(messages.viewJson)}
                                    size="small"
                                    onClick={() => openChangesJson(log)}
                                    sx={{ color: 'primary.main' }}
                                  >
                                    <CodeIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                {log.operationType !== 'insert' && (
                                  <Tooltip title={formatMessage(messages.viewDocumentBefore)} arrow>
                                    <IconButton
                                      aria-label={formatMessage(messages.viewDocumentBefore)}
                                      size="small"
                                      onClick={() => openDocumentBefore(log)}
                                      sx={{ color: 'primary.main' }}
                                    >
                                      <HistoryIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                  />
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>
      <Dialog
        open={jsonDialogOpen}
        onClose={() => setJsonDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{formatMessage(messages.changesJsonTitle)}</DialogTitle>
        <DialogContent dividers>
          <Box
            component="pre"
            sx={{
              m: 0,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              fontFamily: 'Monaco, Menlo, Consolas, "Courier New", monospace',
              fontSize: 12,
            }}
          >
            {jsonDialogContent}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={copyJsonToClipboard}>{formatMessage(messages.copy)}</Button>
          <Button onClick={() => setJsonDialogOpen(false)}>{formatMessage(messages.close)}</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={documentBeforeDialogOpen}
        onClose={() => setDocumentBeforeDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{formatMessage(messages.documentBeforeTitle)}</DialogTitle>
        <DialogContent dividers>
          {documentBeforeLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>{formatMessage(messages.loadingSnapshot)}</Typography>
            </Box>
          ) : documentBeforeError ? (
            <Alert severity="error">{documentBeforeError}</Alert>
          ) : documentBeforeContent ? (
            <Box
              component="pre"
              sx={{
                m: 0,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontFamily: 'Monaco, Menlo, Consolas, "Courier New", monospace',
                fontSize: 12,
              }}
            >
              {documentBeforeContent}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          {documentBeforeContent && (
            <Button onClick={copyDocumentBeforeToClipboard}>{formatMessage(messages.copy)}</Button>
          )}
          <Button onClick={() => setDocumentBeforeDialogOpen(false)}>
            {formatMessage(messages.close)}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AuditLogPage;
